import api from './api';

export interface PartnerRow {
  _id: string;
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  userType: string;
  partnerEntityType?: string;
  isActive: boolean;
  isVerified: boolean;
  isBlocked: boolean;
  blockedReason?: string;
  partnerStatus: string;
  zoneId?: { _id: string; name: string; code?: string };
  serviceHub?: string;
  onboarding?: { status: string; _id: string };
  walletBalance?: number;
  loyaltyPoints?: number;
  pendingDocCount?: number;
  referralCount?: number;
  appVersion?: string;
  referralCode?: string;
  createdAt?: string;
  metadata?: Record<string, unknown>;
}

export async function listPartners(params: {
  entityType?: string;
  status?: string;
  zoneId?: string;
  serviceHub?: string;
  search?: string;
  page?: number;
  limit?: number;
  fromDate?: string;
  toDate?: string;
  partnerSubscriptionPlanId?: string;
}) {
  const res = await api.get('/admin/partners', { params });
  return {
    data: res.data.data as PartnerRow[],
    total: res.data.meta?.total as number | undefined,
  };
}

export async function createPartnerUser(body: Record<string, unknown>) {
  const res = await api.post('/admin/partners', body);
  return res.data.data;
}

export async function updatePartner(id: string, body: Record<string, unknown>) {
  const res = await api.put(`/admin/partners/${id}`, body);
  return res.data.data;
}

export async function togglePartnerActive(id: string, isActive: boolean) {
  const res = await api.put(`/admin/partners/${id}/status`, { isActive });
  return res.data.data;
}

export async function blockPartner(id: string, reason?: string) {
  const res = await api.put(`/admin/partners/${id}/block`, { reason });
  return res.data.data;
}

export async function unblockPartner(id: string) {
  const res = await api.put(`/admin/partners/${id}/unblock`);
  return res.data.data;
}

export async function deletePartner(id: string) {
  const res = await api.delete(`/admin/partners/${id}`);
  return res.data.data;
}

export async function adjustPartnerWallet(id: string, amount: number, type: 'credit' | 'debit', description?: string) {
  const res = await api.post(`/admin/partners/${id}/wallet/adjust`, { amount, type, description });
  return res.data.data;
}

export async function adjustPartnerLoyalty(id: string, points: number, description?: string) {
  const res = await api.post(`/admin/partners/${id}/loyalty/adjust`, { points, description });
  return res.data.data;
}

export interface PartnerReferralSummary {
  referralCode: string;
  totalReferred: number;
  totalEarnings: number;
  bonusPerReferral: number;
  referredUsers: Array<{
    _id: string;
    firstName: string;
    lastName?: string;
    email?: string;
    phone?: string;
    createdAt: string;
    bonusAmount?: number;
  }>;
  history: Array<{
    _id: string;
    description: string;
    amount: number;
    referredUserName: string;
    createdAt: string;
  }>;
}

export async function getPartnerReferrals(id: string) {
  const res = await api.get(`/admin/partners/${id}/referrals`);
  return res.data.data as PartnerReferralSummary;
}

export async function getPartnerGodView(id: string, params?: {
  tab?: string; search?: string; status?: string; from?: string; to?: string; page?: number; limit?: number;
}) {
  const res = await api.get(`/admin/partners/${id}/god-view`, { params });
  return res.data.data as {
    partner: {
      _id: string; name: string; firstName?: string; lastName?: string; email?: string; phone?: string;
      username?: string; status?: string; partnerStatus?: string; userType?: string;
    };
    stats: { rating: number; ratingCount: number; tripCount: number; earnings: number; walletBalance?: number };
    tab: string;
    rows: Record<string, unknown>[];
    total: number;
  };
}

export async function getPartnerReferralSummary(id: string) {
  return getPartnerReferrals(id);
}

export async function getPartnerDocuments(partnerId: string, entityType: string, zoneId?: string, serviceHub?: string) {
  const res = await api.get(`/admin/partners/${partnerId}/documents`, {
    params: { entityType, zoneId, serviceHub },
  });
  return res.data.data;
}

export async function uploadPartnerDocumentForAdmin(
  partnerId: string,
  file: File,
  documentConfigId: string,
  extras?: { documentNumber?: string; expiryDate?: string },
) {
  const form = new FormData();
  form.append('file', file);
  form.append('documentConfigId', documentConfigId);
  if (extras?.documentNumber) form.append('documentNumber', extras.documentNumber);
  if (extras?.expiryDate) form.append('expiryDate', extras.expiryDate);
  const res = await api.post(`/admin/partners/${partnerId}/documents`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.data;
}

export async function verifyPartnerDocument(
  partnerId: string,
  docId: string,
  status: 'approved' | 'rejected',
  rejectionReason?: string
) {
  const res = await api.put(`/admin/partners/${partnerId}/documents/${docId}/verify`, { status, rejectionReason });
  return res.data.data;
}

export async function getMyPartnerProfile() {
  const res = await api.get('/partner/me');
  return res.data.data as {
    user: PartnerRow;
    entityType: string;
    documents: { groups: unknown[]; configs: unknown[]; uploads: unknown[] };
    driverProfile?: Record<string, unknown> | null;
    bankDetails?: Record<string, unknown> | null;
    wallet?: { balance: number; currency: string; isFrozen: boolean };
    loyaltyPoints?: number;
  };
}

export { maskEmail, maskPhone, formatEmail, formatPhone } from '../utils/contactMask';

function resolvePlanZoneId(plan: {
  zoneId?: string | { _id?: string };
  zoneIds?: Array<string | { _id?: string }>;
}) {
  if (plan.zoneId) {
    return typeof plan.zoneId === 'object' ? plan.zoneId._id : plan.zoneId;
  }
  const first = plan.zoneIds?.[0];
  if (!first) return undefined;
  return typeof first === 'object' ? first._id : first;
}

export function partnersUrlForSubscriptionPlan(plan: {
  _id: string;
  name: string;
  partnerType?: string;
  zoneId?: string | { _id?: string };
  zoneIds?: Array<string | { _id?: string }>;
}) {
  const params = new URLSearchParams({
    subscriptionPlanId: plan._id,
    subscriptionPlanName: plan.name,
  });
  if (plan.partnerType && plan.partnerType !== 'all') {
    params.set('entityType', plan.partnerType);
  }
  const zoneIdStr = resolvePlanZoneId(plan);
  if (zoneIdStr) params.set('zoneId', zoneIdStr);
  return `/partners?${params.toString()}`;
}
